/**
 * The single description of every supported search query parameter.
 *
 * Each parameter belongs to exactly one group, and a group knows how to do all three
 * things the search needs:
 *
 * 1. `parse` reads the URL into the applied search (`SearchData`).
 * 2. `serialize` writes a change back to the URL.
 * 3. `toApiParameters` maps the applied search onto the search endpoint's query.
 *
 * Keeping the three together is what stops a parameter from being wired into some of
 * them but not the others. Adding a filter means adding one group here.
 */

import type { LocationQuery } from 'vue-router'
import { AMENITIES } from '~/constants/amenities'
import type { SUPPORTED_SEARCH_QUERY_PARAMETERS } from '~/constants/query-parameters'
import { type REGIONS_CODE, REGIONS } from '~/constants/regions'
import { MOTORIZED_RV_TYPES, RV_TYPES, TOWABLE_RV_TYPES } from '~/constants/rv'
import { SEARCH_SORT_TYPES } from '~/constants/search'
import type { Nullable } from '~/types'
import type { SearchQueryParameters } from '~/types/query-parameters'
import type { RvUnifiedSearchQuery } from '~/types/rental-api-aliases'
import type { SearchData } from '~/types/search'
import type { GeolocationData } from '#shared/types/geolocation'

type SearchApiQuery = NonNullable<RvUnifiedSearchQuery>

/**
 * A query value of `null` renders as a valueless parameter (`?Delivery`), and
 * `undefined` removes the parameter entirely.
 */
type SerializedSearchQuery = Record<string, Nullable<number | string> | undefined>

/**
 * Everything the API mapping needs that is not part of the applied search itself.
 */
export type SearchApiContext = {
  geolocation: Nullable<GeolocationData>
  isInCaOrUs: boolean
}

type SearchParameterGroup = {
  /**
   * The parameters this group owns. A change containing any of them rewrites all of
   * them, which is how a group of related parameters stays consistent in the URL.
   */
  queryParameters: readonly SUPPORTED_SEARCH_QUERY_PARAMETERS[]

  /**
   * Whether these parameters are filters, meaning "clear all filters" resets them.
   * The location, dates, guests and sort parameters are not.
   */
  isFilter?: boolean

  parse?: (query: LocationQuery) => Partial<SearchData>
  serialize: (parameters: SearchQueryParameters) => SerializedSearchQuery
  toApiParameters?: (searchData: SearchData, context: SearchApiContext) => RvUnifiedSearchQuery
}

/**
 * Utility unions that list which search fields and API parameters share a given
 * primitive type. Each alias uses the same mapped-conditional pattern:
 *
 * 1. `[Key in keyof T]-?` walks every key in `T`. The `-?` strips optionality so
 *    optional properties are still considered.
 * 2. `T[Key] extends SomeType ? Key : never` keeps the key name when the property
 *    type matches, otherwise resolves to `never`.
 * 3. `[keyof T]` indexes the mapped type, collapsing the per-key results into one
 *    union and discarding every `never` branch.
 *
 * Example: for `SearchData`, step 2 maps `airportPickup: boolean` to
 * `"airportPickup"`, `amenities: AMENITIES[]` to `never`, and so on. Step 3 yields
 * `"airportPickup" | "delivery" | "instantBook" | ...`.
 *
 * `BooleanSearchDataField` and `NumberSearchDataField` are derived from
 * `SearchData`. `BooleanApiParameter` and `NumberApiParameter` are derived from
 * `SearchApiQuery`; they wrap values in `NonNullable<>` so nullable optional API
 * fields still qualify when their underlying type is boolean or number.
 *
 * These unions constrain the `field` and `apiParameter` arguments in
 * `defineFlagGroup` and `defineNumberGroup`, which index into `SearchData` and
 * `RvUnifiedSearchQuery` dynamically at runtime.
 */
type BooleanSearchDataField = {
  [Field in keyof SearchData]-?: SearchData[Field] extends boolean ? Field : never
}[keyof SearchData]

type NumberSearchDataField = {
  [Field in keyof SearchData]-?: SearchData[Field] extends number | undefined ? Field : never
}[keyof SearchData]

type BooleanApiParameter = {
  [Parameter in keyof SearchApiQuery]-?: NonNullable<SearchApiQuery[Parameter]> extends boolean ? Parameter : never
}[keyof SearchApiQuery]

type NumberApiParameter = {
  [Parameter in keyof SearchApiQuery]-?: NonNullable<SearchApiQuery[Parameter]> extends number ? Parameter : never
}[keyof SearchApiQuery]

/**
 * The API names every RV type it supports individually, and not always the way we do.
 * Declaring the whole record means a new RV type cannot be left unmapped.
 */
const RV_TYPE_API_PARAMETERS: Record<typeof RV_TYPES[number], BooleanApiParameter> = {
  Campervan: 'Campervan',
  ClassA: 'ClassA',
  ClassB: 'ClassB',
  ClassC: 'ClassC',
  FifthWheel: 'FifthWheel',
  Hybrid: 'Hybrid',
  MicroTrailer: 'MicroTrailer',
  TentTrailer: 'TentTrailer',
  ToyHauler: 'ToyHauler',
  TravelTrailer: 'SubTravelTrailer',
  TruckCamper: 'TruckCamper',
}
Object.freeze(RV_TYPE_API_PARAMETERS)

function isRegionCode(code: string): code is REGIONS_CODE {
  return code in REGIONS
}

function getRegionName(code?: Nullable<string>): string | undefined {
  if (!code || !isRegionCode(code)) {
    return undefined
  }

  return REGIONS[code]
}

/**
 * Describes a filter that is either on or off. The parameter carries no value when on
 * and is absent when off.
 */
function defineFlagGroup<
  Field extends BooleanSearchDataField,
  ApiParam extends BooleanApiParameter,
>({ queryParameter, field, apiParameter }: {
  queryParameter: SUPPORTED_SEARCH_QUERY_PARAMETERS
  field: Field
  apiParameter: ApiParam
}): SearchParameterGroup {
  return {
    queryParameters: [queryParameter],
    isFilter: true,
    parse: (query) => {
      const result: Partial<SearchData> = {}
      result[field] = getQueryParameterFlag(query, queryParameter)
      return result
    },
    serialize: (parameters) => ({ [queryParameter]: parameters[queryParameter] ? null : undefined }),
    toApiParameters: (searchData) => {
      if (!searchData[field]) {
        return {}
      }

      const result: RvUnifiedSearchQuery = {}
      result[apiParameter] = true
      return result
    },
  }
}

/**
 * Describes a filter made of numeric bounds, such as a price range. Passing either
 * bound rewrites both, so a range is never half applied.
 * @param bounds - The bounds to define the number group.
 */
function defineNumberGroup(bounds: readonly {
  queryParameter: SUPPORTED_SEARCH_QUERY_PARAMETERS
  field: NumberSearchDataField
  apiParameter: NumberApiParameter
}[]): SearchParameterGroup {
  return {
    queryParameters: bounds.map(({ queryParameter }) => queryParameter),
    isFilter: true,
    parse: (query) => {
      const result: Partial<SearchData> = {}

      for (const { queryParameter, field } of bounds) {
        result[field] = getQueryParameterNumber(query, queryParameter)
      }

      return result
    },
    serialize: (parameters) => {
      const result: SerializedSearchQuery = {}

      for (const { queryParameter } of bounds) {
        const value = parameters[queryParameter]
        result[queryParameter] = typeof value === 'number' ? value : undefined
      }

      return result
    },
    // A zero bound is treated as unset, which is what keeps a minimum of zero out of the request.
    toApiParameters: (searchData) => {
      const result: RvUnifiedSearchQuery = {}

      for (const { field, apiParameter } of bounds) {
        const value = searchData[field]

        if (value) {
          result[apiParameter] = value
        }
      }

      return result
    },
  }
}

export const SEARCH_PARAMETER_GROUPS: readonly SearchParameterGroup[] = [
  /**
   * Where to search, in priority order: the map area, then the resolved place, then the
   * user's own location. All six parameters move together so that picking a place
   * leaves the map area behind, and vice versa.
   *
   * The place itself is resolved from `PlaceId` or `SearchAddress` by the search
   * middleware, since that needs a request.
   */
  {
    queryParameters: ['PlaceId', 'SearchAddress', 'NeLat', 'NeLng', 'SwLat', 'SwLng'],
    parse: (query) => {
      const neLat = getQueryParameterNumber(query, 'NeLat')
      const neLng = getQueryParameterNumber(query, 'NeLng')
      const swLat = getQueryParameterNumber(query, 'SwLat')
      const swLng = getQueryParameterNumber(query, 'SwLng')

      if (neLat === undefined || neLng === undefined || swLat === undefined || swLng === undefined) {
        return { bounds: undefined }
      }

      return { bounds: { neLat, neLng, swLat, swLng } }
    },
    serialize: ({ PlaceId, SearchAddress, NeLat, NeLng, SwLat, SwLng }) => ({
      PlaceId,
      SearchAddress,
      NeLat,
      NeLng,
      SwLat,
      SwLng,
    }),
    toApiParameters: ({ bounds, location }, { geolocation, isInCaOrUs }) => {
      if (bounds) {
        return {
          NeLat: bounds.neLat,
          NeLng: bounds.neLng,
          SwLat: bounds.swLat,
          SwLng: bounds.swLng,
        }
      }

      if (location?.coordinates) {
        const parameters: RvUnifiedSearchQuery = { Country: location.countryCode ?? location.code ?? undefined }

        if (location.type === 'Region') {
          parameters.Region = getRegionName(location.code)
        }
        else if (location.type !== 'Country') {
          parameters.SearchLat = location.coordinates.lat
          parameters.SearchLng = location.coordinates.lng
        }

        return parameters
      }

      if (isInCaOrUs) {
        const parameters: RvUnifiedSearchQuery = { Country: geolocation?.countryCode }

        if (geolocation?.latitude && geolocation?.longitude) {
          parameters.SearchLat = geolocation.latitude
          parameters.SearchLng = geolocation.longitude
        }

        return parameters
      }

      return {}
    },
  },

  // A partial range is no range at all, since the search endpoint needs both ends.
  {
    queryParameters: ['StartDate', 'EndDate'],
    parse: (query) => {
      const start = getQueryParameter(query, 'StartDate')
      const end = getQueryParameter(query, 'EndDate')

      return { dates: start && end ? { start, end } : undefined }
    },
    serialize: ({ StartDate, EndDate }) => ({ StartDate, EndDate }),
    toApiParameters: ({ dates }) => (dates ? { StartDate: dates.start, EndDate: dates.end } : {}),
  },

  /**
   * The guest counts. `PetFriendly` is part of the same selection but is its own group,
   * so that toggling the pet filter does not clear the counts.
   */
  {
    queryParameters: ['Adults', 'Children'],
    parse: (query) => {
      const adults = getQueryParameterNumber(query, 'Adults')
      const children = getQueryParameterNumber(query, 'Children')
      const petFriendly = getQueryParameterFlag(query, 'PetFriendly')

      if (adults === undefined && children === undefined && !petFriendly) {
        return { guests: undefined }
      }

      return {
        guests: {
          adults: adults ?? (petFriendly ? 1 : 0),
          children: children ?? 0,
          petFriendly,
        },
      }
    },
    serialize: ({ Adults, Children }) => ({ Adults, Children }),
    toApiParameters: ({ guests }) => (guests ? { Guest: guests.adults + guests.children } : {}),
  },

  // Parsed with the guest counts, and sent to the API as an amenity.
  {
    queryParameters: ['PetFriendly'],
    isFilter: true,
    serialize: ({ PetFriendly }) => ({ PetFriendly: PetFriendly ? null : undefined }),
  },

  /**
   * The RV types. `Drivable` and `Towable` are shorthands for selecting a whole
   * category, and are never written to the URL themselves.
   */
  {
    queryParameters: ['Types', 'Drivable', 'Towable'],
    isFilter: true,
    parse: (query) => {
      const types = getQueryParameterList(query, 'Types')

      return { types: RV_TYPES.filter((type) => types.includes(type)) }
    },
    serialize: ({ Types, Drivable, Towable }) => ({
      Types: [
        ...MOTORIZED_RV_TYPES.filter((type) => Drivable || Types?.includes(type)),
        ...TOWABLE_RV_TYPES.filter((type) => Towable || Types?.includes(type)),
      ].join(',') || undefined,
    }),
    toApiParameters: ({ types }) => {
      const result: RvUnifiedSearchQuery = {}

      for (const type of RV_TYPES) {
        result[RV_TYPE_API_PARAMETERS[type]] = types.includes(type) || undefined
      }

      return result
    },
  },

  /**
   * The amenities, which is also how the API receives the pet friendly and airport
   * pickup filters even though those have their own query parameters.
   */
  {
    queryParameters: ['Amenities'],
    isFilter: true,
    parse: (query) => {
      const amenities = getQueryParameterList(query, 'Amenities')

      return {
        amenities: AMENITIES.filter((amenity) => amenities.includes(amenity)),
        airportPickup: amenities.includes('AirportPickup') || getQueryParameterFlag(query, 'AirportPickup'),
      }
    },
    serialize: ({ Amenities }) => ({
      Amenities: AMENITIES.filter((amenity) => Amenities?.includes(amenity)).join(',') || undefined,
    }),
    toApiParameters: ({ airportPickup, amenities, guests }) => {
      const values = [
        ...amenities,
        guests?.petFriendly ? 'PetFriendly' : undefined,
        airportPickup ? 'AirportPickup' : undefined,
      ].filter(Boolean)

      return values.length ? { Amenities: values.join(',') } : {}
    },
  },

  // Parsed and sent to the API with the amenities.
  {
    queryParameters: ['AirportPickup'],
    isFilter: true,
    serialize: ({ AirportPickup }) => ({ AirportPickup: AirportPickup ? null : undefined }),
  },

  {
    queryParameters: ['RVBrand'],
    isFilter: true,
    parse: (query) => ({ rvBrand: getQueryParameter(query, 'RVBrand')?.trim() || undefined }),
    serialize: ({ RVBrand }) => ({ RVBrand: RVBrand || undefined }),
    toApiParameters: ({ rvBrand }) => (rvBrand ? { RVBrand: rvBrand } : {}),
  },

  {
    queryParameters: ['SortOrder'],
    parse: (query) => {
      const sortOrder = getQueryParameter(query, 'SortOrder')

      return {
        sortOrder: Object.values(SEARCH_SORT_TYPES).find((sortType) => sortType === sortOrder)
          ?? 'Recommended',
      }
    },
    serialize: ({ SortOrder }) => ({ SortOrder }),
    toApiParameters: ({ sortOrder }) => ({ SortOrder: sortOrder }),
  },

  defineFlagGroup({ queryParameter: 'Delivery', field: 'delivery', apiParameter: 'Delivery' }),
  defineFlagGroup({ queryParameter: 'InstantBook', field: 'instantBook', apiParameter: 'Instabook' }),
  defineFlagGroup({ queryParameter: 'UnlimitedMileage', field: 'unlimitedMileage', apiParameter: 'UnlimitedMileage' }),
  defineFlagGroup({ queryParameter: 'FestivalFriendly', field: 'festivalFriendly', apiParameter: 'FestivalFriendly' }),
  defineFlagGroup({ queryParameter: 'ExperienceNotRequired', field: 'experienceNotRequired', apiParameter: 'ExperienceNotRequired' }),
  defineFlagGroup({ queryParameter: 'IsSuperhost', field: 'isSuperhost', apiParameter: 'IsSuperHost' }),

  defineNumberGroup([
    { queryParameter: 'MinPrice', field: 'minPrice', apiParameter: 'MinPrice' },
    { queryParameter: 'MaxPrice', field: 'maxPrice', apiParameter: 'MaxPrice' },
  ]),
  defineNumberGroup([
    { queryParameter: 'MinWeight', field: 'minWeight', apiParameter: 'MinWeight' },
    { queryParameter: 'MaxWeight', field: 'maxWeight', apiParameter: 'MaxWeight' },
  ]),
  defineNumberGroup([
    { queryParameter: 'MinLength', field: 'minLength', apiParameter: 'MinLength' },
    { queryParameter: 'MaxLength', field: 'maxLength', apiParameter: 'MaxLength' },
  ]),
  defineNumberGroup([
    { queryParameter: 'MinRvYear', field: 'minYear', apiParameter: 'MinYear' },
    { queryParameter: 'MaxRvYear', field: 'maxYear', apiParameter: 'MaxYear' },
  ]),
]
Object.freeze(SEARCH_PARAMETER_GROUPS)

/**
 * Every parameter that "clear all filters" resets.
 */
export const SEARCH_FILTER_QUERY_PARAMETERS: readonly SUPPORTED_SEARCH_QUERY_PARAMETERS[] = SEARCH_PARAMETER_GROUPS
  .filter(({ isFilter }) => isFilter)
  .flatMap(({ queryParameters }) => queryParameters)
Object.freeze(SEARCH_FILTER_QUERY_PARAMETERS)

/**
 * The applied search before any of it has been read from the URL. Also the starting
 * point for parsing, so a parameter that is absent from the URL always resets rather
 * than keeping whatever the previous search had.
 */
export function createDefaultSearchData(): SearchData {
  return {
    airportPickup: false,
    amenities: [],
    bounds: undefined,
    dates: undefined,
    delivery: false,
    experienceNotRequired: false,
    festivalFriendly: false,
    guests: undefined,
    instantBook: false,
    isSuperhost: false,
    location: undefined,
    maxLength: undefined,
    maxPrice: undefined,
    maxWeight: undefined,
    maxYear: undefined,
    minLength: undefined,
    minPrice: undefined,
    minWeight: undefined,
    minYear: undefined,
    rvBrand: undefined,
    sortOrder: 'Recommended',
    types: [],
    unlimitedMileage: false,
  }
}

/**
 * Reads a URL query into the applied search.
 *
 * The location is left unresolved, since turning a `PlaceId` or `SearchAddress` into a
 * place requires a request.
 * @param query - The query to parse.
 */
export function parseSearchQuery(query: LocationQuery): SearchData {
  const searchData = createDefaultSearchData()

  for (const { parse } of SEARCH_PARAMETER_GROUPS) {
    Object.assign(searchData, parse?.(query))
  }

  return searchData
}

/**
 * Turns a change to the search into the query parameters that express it.
 *
 * Only the groups the caller mentioned are written, so a navigation that is not about
 * dates keeps the current dates, and a parameter passed as undefined is removed.
 * @param parameters - The parameters to serialize.
 */
export function serializeSearchParameters(parameters: SearchQueryParameters): SerializedSearchQuery {
  const query: SerializedSearchQuery = {}

  for (const { queryParameters, serialize } of SEARCH_PARAMETER_GROUPS) {
    if (queryParameters.some((queryParameter) => queryParameter in parameters)) {
      Object.assign(query, serialize(parameters))
    }
  }

  return query
}

/**
 * Maps the applied search onto the search endpoint's query.
 * @param searchData - The search data to map.
 * @param context - The context to map the search data to.
 */
export function getSearchApiParameters(searchData: SearchData, context: SearchApiContext): RvUnifiedSearchQuery {
  const parameters: RvUnifiedSearchQuery = {}

  for (const { toApiParameters } of SEARCH_PARAMETER_GROUPS) {
    Object.assign(parameters, toApiParameters?.(searchData, context))
  }

  return parameters
}
